import { Controller } from "@hotwired/stimulus";
import { i18n } from "../../lib/i18n";

/**
 * Field Mapping Controller
 *
 * Manages the UI for mapping a single CSV field to multiple system fields.
 * Handles adding, removing, and updating additional field mappings.
 */
export default class extends Controller {
  static targets = ["primarySelect", "additionalFields", "container"];
  static classes = ["multiple"];

  connect() {
    // Initialize controller
    this.updateVisualState();
  }

  /**
   * Add a new field mapping for the current CSV column
   */
  addField(event) {
    event.preventDefault();

    // Get the CSV column name from the primary select
    const csvColumn = this.primarySelectTarget.name;

    // Create a new select element by cloning the primary select
    const newSelect = this.primarySelectTarget.cloneNode(true);

    // Reset the selection and update attributes
    newSelect.value = "";
    // Make sure the name attribute is correctly formatted
    // It should be in the format: import[field_mappings][csv_column][]
    // This ensures Rails properly processes it as an array
    newSelect.name = this.primarySelectTarget.name;
    // Remove the field mapping target attribute
    delete newSelect.dataset.fieldMappingTarget;

    // Create the remove button
    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = "btn btn-ghost btn-sm";
    removeButton.dataset.action = "imports--field-mapping#removeField";
    removeButton.innerHTML = `
      <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    `;

    // Create a new field mapping row
    const newFieldRow = document.createElement("div");
    newFieldRow.className = "flex items-center gap-2 mt-2";
    newFieldRow.appendChild(newSelect);
    newFieldRow.appendChild(removeButton);

    // Add the new row to the additional fields container
    this.additionalFieldsTarget.appendChild(newFieldRow);

    // Trigger auto-save to persist the new mapping
    this.triggerAutoSave();
  }

  /**
   * Remove a field mapping
   */
  removeField(event) {
    event.preventDefault();

    // Get the field mapping row to remove
    const fieldRow = event.currentTarget.closest("div");

    // Remove the field mapping row
    fieldRow.remove();

    // Trigger auto-save to persist the changes
    this.triggerAutoSave();
  }

  /**
   * Trigger auto-save to persist changes
   */
  triggerAutoSave() {
    const form = this.element.closest("form");
    const autoSaveController =
      this.application.getControllerForElementAndIdentifier(form, "auto-save");
    if (autoSaveController) {
      autoSaveController.submitForm();
    }

    // Update visual state after saving
    this.updateVisualState();
  }

  /**
   * Update the visual state of the field mapping row based on the number of mappings
   */
  updateVisualState() {
    // Check if we have multiple mappings
    const hasMultipleMappings = this.additionalFieldsTarget.children.length > 0;

    // Update the add button text based on whether we have mappings already
    const addButton = this.element.querySelector(
      '[data-action="imports--field-mapping#addField"]',
    );
    if (addButton) {
      const buttonText = hasMultipleMappings
        ? i18n.t('imports.add_another_mapping')
        : i18n.t('imports.map_to_multiple_fields');
      const buttonSpan = addButton.querySelector("span");
      if (buttonSpan) {
        buttonSpan.innerHTML = `
          <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          ${buttonText}
        `;
      }
    }
  }
}
